import React from "react";
import { Box, Button, Flex, Text, CloseButton } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

export interface TourGuideTooltipProps {
  title: string;
  description: string;
  currentStepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onBack?: () => void;
  onClose: () => void;
  variant?: "tour" | "hover";
}

export const TourGuideTooltip: React.FC<TourGuideTooltipProps> = ({
  title,
  description,
  currentStepIndex,
  totalSteps,
  onNext,
  onBack,
  onClose,
  variant = "tour",
}) => {
  const { t } = useTranslation();
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;

  if (variant === "hover") {
    return (
      <Box
        bg="white"
        borderRadius="12px"
        border="1px solid #E5E5E5"
        boxShadow="0px 2px 12px rgba(0, 0, 0, 0.1)"
        p={4}
        minW="280px"
        maxW="320px"
      >
        <Text fontSize="14px" fontWeight="600" mb={2} color="#1A1A1A">
          {title}
        </Text>
        <Text fontSize="12px" color="#666666" lineHeight="1.5">
          {description}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      bg="white"
      borderRadius="12px"
      boxShadow="0px 4px 20px rgba(0, 0, 0, 0.15)"
      p={5}
      minW="360px"
      maxW="420px"
      position="relative"
    >
      {/* Top Row: Title and Close Button */}
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontSize="16px" fontWeight="600" color="#1A1A1A">
          {title}
        </Text>
        <CloseButton
          size="sm"
          onClick={onClose}
          ml={3}
        />
      </Flex>

      {/* Description */}
      <Text fontSize="13px" color="#666666" mb={5} lineHeight="1.6">
        {description}
      </Text>

      {/* Bottom Row: Progress (left) and Navigation Buttons (right) */}
      <Flex justify="space-between" align="center">
        {/* Progress - Bottom Left */}
        <Text fontSize="12px" color="#999999" fontWeight="500">
          {t("tourGuide.progress", {
            current: currentStepIndex + 1,
            total: totalSteps,
          })}
        </Text>

        {/* Navigation Buttons - Bottom Right */}
        <Flex gap={2}>
          {!isFirstStep && onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              fontSize="12px"
              h="32px"
              color="#5C6670"
              _hover={{ bg: "#F5F5F5" }}
            >
              {t("tourGuide.back")}
            </Button>
          )}
          <Button
            bg="#1819B4"
            color="white"
            size="sm"
            onClick={onNext}
            fontSize="12px"
            h="32px"
            _hover={{ bg: "#1415A0" }}
            _active={{ bg: "#0F1180" }}
          >
            {isLastStep ? t("tourGuide.finish") : t("tourGuide.next")}
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};
